import { useEffect, useState } from 'react';
import { User, MapPin, Phone, Mail, Lock } from 'lucide-react';
import { LocationInput } from '@/components/location/LocationInput';
import { useAuthStore } from '@/store/authStore';

interface Props {
  data: any;
  onChange: (data: any) => void;
}

export function PersonalInfoStep({ data, onChange }: Props) {
  const { user } = useAuthStore();
  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    businessLocation: ''
  });
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    phone: false,
    businessLocation: false
  });

  // Auto-fill fields from signup data on mount
  useEffect(() => {
    if (user && (!data.firstName || !data.lastName || !data.email || !data.phone)) {
      onChange({
        ...data,
        firstName: data.firstName || user.firstName || '',
        lastName: data.lastName || user.lastName || '',
        email: data.email || user.email || '',
        phone: data.phone || user.phone || ''
      });
    }
  }, [user]);

  const validateField = (field: string, value: any) => {
    let error = '';
    
    switch (field) {
      case 'firstName':
        if (!value?.trim()) error = 'First name is required';
        break;
      case 'lastName':
        if (!value?.trim()) error = 'Last name is required';
        break;
      case 'phone':
        if (!value?.trim()) error = 'Phone number is required';
        else if (!/^[\d\s\+\-\(\)]+$/.test(value)) error = 'Invalid phone number format';
        break;
      case 'businessLocation':
        if (!value?.trim()) error = 'Business location is required';
        break;
    }
    
    setErrors(prev => ({ ...prev, [field]: error }));
    return error === '';
  };

  const updateField = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
    if (touched[field as keyof typeof touched]) {
      validateField(field, value);
    }
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, data[field]);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Personal Information</h2>
        <p className="text-gray-600">Tell us about yourself</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <User className="w-4 h-4 inline mr-2" />
            First Name *
          </label>
          <input
            type="text"
            value={data.firstName || ''}
            onChange={(e) => updateField('firstName', e.target.value)}
            onBlur={() => handleBlur('firstName')}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.firstName && touched.firstName ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.firstName && touched.firstName && (
            <p className="text-xs text-red-600 mt-1">{errors.firstName}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Last Name *
          </label>
          <input
            type="text"
            value={data.lastName || ''}
            onChange={(e) => updateField('lastName', e.target.value)}
            onBlur={() => handleBlur('lastName')}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.lastName && touched.lastName ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.lastName && touched.lastName && (
            <p className="text-xs text-red-600 mt-1">{errors.lastName}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Mail className="w-4 h-4 inline mr-2" />
            Email Address *
          </label>
          <div className="relative">
            <input
              type="email"
              value={data.email || ''}
              onChange={(e) => updateField('email', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
              disabled
            />
            <Lock className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>
          <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Phone className="w-4 h-4 inline mr-2" />
            Phone Number *
          </label>
          <input
            type="tel"
            value={data.phone || ''}
            onChange={(e) => updateField('phone', e.target.value)}
            onBlur={() => handleBlur('phone')}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.phone && touched.phone ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.phone && touched.phone && (
            <p className="text-xs text-red-600 mt-1">{errors.phone}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <MapPin className="w-4 h-4 inline mr-2" />
          Business Location *
        </label>
        <div className={`${errors.businessLocation && touched.businessLocation ? 'ring-2 ring-red-500 rounded-lg' : ''}`}>
          <LocationInput
            value={data.businessLocation || ''}
            onChange={(value) => updateField('businessLocation', value)}
            placeholder="Enter your business address"
          />
        </div>
        {errors.businessLocation && touched.businessLocation && (
          <p className="text-xs text-red-600 mt-1">{errors.businessLocation}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          This is your business address, not your service areas
        </p>
      </div>
    </div>
  );
}
